import { __ } from "@wordpress/i18n";
import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    ButtonGroup,
    NumberInput,
    ColorInput,
    SelectInput,
} from "../ui/index";

const ZOOM_TYPES = [
    { value: "lens",   label: __("Lens", "wpbean-product-gallery-slider-for-woocommerce"),   premium: true },
    { value: "window", label: __("Window", "wpbean-product-gallery-slider-for-woocommerce"), premium: true },
    { value: "inner",  label: __("Inner", "wpbean-product-gallery-slider-for-woocommerce") },
];

const LENS_SHAPES = [
    { value: "circle", label: __("Circle", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "square", label: __("Square", "wpbean-product-gallery-slider-for-woocommerce") },
];

const CURSORS = [
    { value: "crosshair", label: __("Crosshair", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "zoom-in", label: __("Zoom-in", "wpbean-product-gallery-slider-for-woocommerce") },
    { value: "default", label: __("Default", "wpbean-product-gallery-slider-for-woocommerce") },
];

export default function ZoomPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title={__("Activation", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Control when zoom is triggered.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Zoom on Hover", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Activate zoom when the cursor hovers over the main image.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <Toggle
                        value={settings.zoomOnHover}
                        onChange={(v) => s("zoomOnHover", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Cursor Style", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Mouse cursor appearance over the main image.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <SelectInput
                        options={CURSORS}
                        value={settings.zoomCursor}
                        onChange={(v) => s("zoomCursor", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title={__("Zoom Type", "wpbean-product-gallery-slider-for-woocommerce")}
                description={__("Choose how the magnified image is displayed.", "wpbean-product-gallery-slider-for-woocommerce")}
            >
                <SettingRow
                    label={__("Display Mode", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Lens overlays the image, Window shows a separate panel, Inner zooms inside the image.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <ButtonGroup
                        options={ZOOM_TYPES}
                        value={settings.zoomType}
                        onChange={(v) => s("zoomType", v)}
                    />
                </SettingRow>

                <SettingRow
                    label={__("Zoom Level", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Magnification multiplier applied to the full-size image.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <NumberInput
                        value={settings.zoomLevel}
                        onChange={(v) => s("zoomLevel", v)}
                        min={1}
                        max={10}
                        step={0.5}
                    />
                </SettingRow>
            </SectionCard>

            {settings.zoomType !== "inner" && (
                <SectionCard
                    title={__("Easing", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Smooth the zoom movement for a polished feel.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <SettingRow
                        label={__("Enable Easing", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Apply a smooth follow-through to zoom position updates.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <Toggle
                            value={settings.zoomEasing}
                            onChange={(v) => s("zoomEasing", v)}
                        />
                    </SettingRow>

                    {settings.zoomEasing && (
                        <SettingRow
                            label={__("Easing Amount", "wpbean-product-gallery-slider-for-woocommerce")}
                            description={__("Easing lag factor. Higher values produce a slower, more fluid follow.", "wpbean-product-gallery-slider-for-woocommerce")}
                        >
                            <NumberInput
                                value={settings.zoomEasingAmount}
                                onChange={(v) => s("zoomEasingAmount", v)}
                                min={1}
                                max={50}
                            />
                        </SettingRow>
                    )}
                </SectionCard>
            )}

            {settings.zoomType === "lens" && (
                <SectionCard
                    title={__("Lens Appearance", "wpbean-product-gallery-slider-for-woocommerce")}
                    description={__("Customize the zoom lens overlay.", "wpbean-product-gallery-slider-for-woocommerce")}
                >
                    <SettingRow
                        label={__("Lens Size", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Diameter or width of the circular/square lens in pixels.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <NumberInput
                            value={settings.lensSize}
                            onChange={(v) => s("lensSize", v)}
                            min={50}
                            max={500}
                            unit="px"
                        />
                    </SettingRow>

                    <SettingRow
                        label={__("Lens Shape", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Visual shape of the zoom lens.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <ButtonGroup
                            options={LENS_SHAPES}
                            value={settings.lensShape}
                            onChange={(v) => s("lensShape", v)}
                        />
                    </SettingRow>

                    <SettingRow
                        label={__("Border Color", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Color of the lens border ring.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <ColorInput
                            value={settings.lensBorderColor}
                            onChange={(v) => s("lensBorderColor", v)}
                        />
                    </SettingRow>

                    <SettingRow
                        label={__("Border Width", "wpbean-product-gallery-slider-for-woocommerce")}
                        description={__("Thickness of the lens border in pixels.", "wpbean-product-gallery-slider-for-woocommerce")}
                    >
                        <NumberInput
                            value={settings.lensBorderWidth}
                            onChange={(v) => s("lensBorderWidth", v)}
                            min={0}
                            max={20}
                            unit="px"
                        />
                    </SettingRow>
                </SectionCard>
            )}
        </div>
    );
}
